import React, {forwardRef, useCallback, useEffect, useState} from 'react';

import {UniqueIdentifier} from '@dnd-kit/core';
import {observer} from 'mobx-react-lite';
import 'react-loading-skeleton/dist/skeleton.css';
import {ContainerProps} from '../../../../entity/props/document/container-props';
import {useService} from '../../../../infrastructure/service/service';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Handle} from '../widget/Handle';
import {Section} from '../../../../entity/resource/section';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import {CircularProgress, IconButton, Menu, MenuItem, Typography} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ConfirmDialog from '../widget/ConfirmDialog';
import {CloudUpload} from '@mui/icons-material';
import {useDropzone} from 'react-dropzone';
import {Title} from '../../../../entity/model/title';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {DocumentDto, TitleTemplateDto} from '../../../../entity/dto/title-template-dto';
import {Document} from '../../../../entity/resource/document';
import {toast} from 'react-toastify';

import '../../../../assets/css/style.css';
import '../../../../assets/css/document/container.css';

const Container = forwardRef<any, ContainerProps>((props: ContainerProps, ref: React.ForwardedRef<any>): JSX.Element => {
  const {sectionService, projectService, titleService, documentService, titleTemplateService} = useService();

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [loadingConfirmBtn, setLoadingConfirmBtn] = useState<boolean>(false);
  const [isPermitted, setPermitted] = useState<boolean>(false);
  const [title, setTitle] = useState<Title | undefined>(titleService.getTitle());
  const [uploading, setUploading] = useState<boolean>(false);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [documentIndex, setDocumentIndex] = useState<number>(0);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);

  const Component = 'div';

  const onDrop = useCallback(async (acceptedFiles: any): Promise<void> => {
    const pdfFiles = acceptedFiles.filter((file: any): boolean => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      toast.warning('Only PDF files are allowed.');
      return;
    }

    setUploading(true);
    setDocumentCount(pdfFiles.length);
    handleMenuClose();

    const batchSize = 3;
    const batches = [];
    const templateDocuments: DocumentDto[] = [];

    for (let i = 0; i < pdfFiles.length; i += batchSize)
      batches.push(pdfFiles.slice(i, i + batchSize));

    for (const [batchIndex, batch] of batches.entries()) {
      await Promise.all(
        batch.map(async (file: any, index: number) => {
          const globalIndex = batchIndex * batchSize + index + 1;
          setDocumentIndex(globalIndex);

          if (props.project) {
            await documentService.uploadFile(
              props.project.createdByOrganizationID,
              file,
              title?.id,
              sectionService.findSectionById(title?.sections ?? [], props.label)?.id);

            const updatedTitle = await titleService.getTitleById(props.project?.createdByOrganizationID ?? '', title?.id ?? '');
            if(updatedTitle) {
              setTitle(updatedTitle);
              titleService.setTitle(updatedTitle);
            }
          } else {
            const response = await documentService.uploadFile(
              titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '',
              file,
              undefined,
              sectionService.findSectionById(titleTemplateService.getTitleTemplate()?.sections ?? [], props.label)?.id,
              undefined,
              titleTemplateService.getTitleTemplate()?.id ?? '');

            if (response)
              templateDocuments.push(response);
          }
        })
      );
    }

    if (!props.project) {
      const sections = titleTemplateService.getTitleTemplate()?.sections.map((section: Section)=> ({
        id: section.id,
        name: section.name,
        documents: [
          ...section.documents.map((document: Document) => ({
            id: document.id,
            name: document.name,
            url: document.url
          })),
          ...(section.id === props.label
            ? templateDocuments.map((documentDto: DocumentDto) => ({
              id: '',
              name: documentDto.name,
              url: documentDto.url
            }))
            : [])
        ]
      }));

      const templatePayload: TitleTemplateDto = {
        name: titleTemplateService.getTitleTemplate()?.name ?? '',
        titleID: titleTemplateService.getTitleTemplate()?.titleID ?? '',
        address: titleTemplateService.getTitleTemplate()?.address ?? '',
        stateID: titleTemplateService.getTitleTemplate()?.stateID ?? '',
        countyID: titleTemplateService.getTitleTemplate()?.countyID ?? '',
        subdivision: titleTemplateService.getTitleTemplate()?.subdivision ?? '',
        sectionIDs: [],
        documentIDs: [],
        sections: sections ?? [],
        tmsParcelNumbers: titleTemplateService.getTitleTemplate()?.tmsParcelNumbers ?? [],
        isPublic: titleTemplateService.getTitleTemplate()?.isPublic ?? true,
        isProjectTitle: true
      };

      await titleTemplateService.updateTitleTemplate(titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '', titleTemplateService.getTitleTemplate()?.id ?? '', templatePayload);
      const updatedTitleTemplate = await titleTemplateService.getTitleTemplateById(titleTemplateService.getTitleTemplate()?.createdByOrganizationID ?? '', titleTemplateService.getTitleTemplate()?.id ?? '', true);
      const items = updatedTitleTemplate?.sections.reduce((acc: any, section: Section) => {
        acc[section.id] = section.documents.map((doc) => doc.id);
        return acc;
      }, {});

      props.setItems(items);
      if (items && Object.keys(items).length)
        sectionService.setMovableItems(items);
    }

    setUploading(false);
  }, [])

  const { getRootProps, getInputProps } = useDropzone({onDrop, useFsAccessApi: false});

  useEffect(() => {
    setPermitted(title ? projectService.isPermittedTitle(title.id) : true);
  }, []);

  const handleDeleteSection = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true);
  }

  const deleteSection = async (): Promise<void> => {
    setLoadingConfirmBtn(true);

    if (props.project) {
      const response = await sectionService.deleteSection(props.project.createdByOrganizationID, props.label ?? '',title?.id ?? '');
      if (response) {
        const newTitle = await titleService.getTitleById(props.project.createdByOrganizationID, title?.id ?? '');
        props.setUpdatedSections && props.setUpdatedSections(newTitle?.sections?.filter((section: Section): boolean => section.name !== 'Unclassified') ?? []);
        props.setItems(sectionService.getItems(newTitle?.sections ?? []));

        if (newTitle)
          titleService.setTitle(newTitle);
      }
    } else {
      let template = titleTemplateService.getTitleTemplate();
      const response = await sectionService.deleteSection(template?.createdByOrganizationID ?? '', props.label ?? '', undefined, template?.id ?? '');
      if (response) {
        template = await titleTemplateService.getTitleTemplateById(template?.createdByOrganizationID ?? '', template?.id ?? '', true);
        props.setUpdatedSections && props.setUpdatedSections(template?.sections?.filter((section: Section): boolean => section.name !== 'Unclassified') ?? []);
        props.setItems(sectionService.getItems(template?.sections ?? []));
      }
    }

    setLoadingConfirmBtn(false);
    setOpenConfirmDialog(false);
  }

  const handleExpandSection = (): void => {
    sectionService.setExpandSingleContainer(true);
    sectionService.addToExpandedSectionIds(props.label ?? '');
  }

  const handleShrinkSection = (): void => {
    sectionService.setExpandSingleContainer(false);
    sectionService.removeExpandedSectionIds(props.label ?? '');
  }

  const removePaddingApply = (): boolean => {
    if (!props.isNonSection)
      if(sectionService.getExpandAllContainers())
        return false;
      else
        return sectionService.getExpandSectionIds().length > 0
          ? !sectionService.getExpandSectionIds().includes(props.label as UniqueIdentifier)
          : true;

    return false;
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setMenuElement(event.currentTarget);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const renderSectionMenu = (): JSX.Element => {
    return (
      <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
        <Box {...getRootProps()} className={'btn-container'}>
          <input id={'section-upload'} hidden {...getInputProps()} accept={'.pdf'}/>
          <MenuItem disableRipple className={'zitles-menu-item'}>
            <CloudUpload className={'zitles-icon'}/>
            <Typography className={'zitles-menu-item button'}>Upload</Typography>
          </MenuItem>
        </Box>
        {
          props.onRemove &&
            <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleDeleteSection}>
              <DeleteIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Delete</Typography>
            </MenuItem>
        }
      </Menu>
    );
  }

  return (
    <>
      <Component id={'draggable-container'} ref={ref} className={props.className} style={props.style && props.style}>
        {
          props.label
            ? <Box className={`zitles-container ${sectionService.getExpandSectionIds().includes(props.label) ? 'selected' : ''}`}>
                <Box className={'zitles-container-head'}>
                  <Handle id={'section-drag-btn'} disabled={!isPermitted} {...props.handleProps} />
                  {
                    sectionService.getExpandSectionIds().includes(props.label)
                      ? <ArrowDropDownIcon id={'section-shrink-btn'} className={'icon-button dynamic'} onClick={handleShrinkSection}/>
                      : <ArrowRightIcon id={'section-expand-btn'} className={'icon-button dynamic'} onClick={handleExpandSection}/>
                  }
                  <span id={'text-section-name'}>{sectionService.findSectionById(props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? [], props.label)?.name ?? ''} ({props.itemCount})</span>
                </Box>
                {
                  uploading
                    ? <Box className={'uploading-container'}>
                        <CircularProgress id={'upload-progress'} size={16} className={'upload-progress-icon'}/>
                        <span id={'text-upload-status'} className={'upload-status'}>Uploading {documentIndex} of {documentCount}</span>
                      </Box>
                    : <Box>
                        {
                          sectionService.getMovingSectionId() === props.label && sectionService.getSectionMoving()
                            ? <CircularProgress id={'section-loading-icon'} size={16} color='inherit' className={'loading-icon'}/>
                            : <Box>
                                <IconButton
                                  id={'section-menu'}
                                  className={`zitles-menu-trigger${!isPermitted || uploading ? 'disabled' : ''}`}
                                  color= 'inherit'
                                  disableRipple
                                  disableFocusRipple
                                  onClick={(event: React.MouseEvent<HTMLElement>): boolean | void => (isPermitted && !uploading) && handleMenuOpen(event)}>
                                  <MoreHorizIcon />
                                </IconButton>
                                {
                                  renderSectionMenu()
                                }
                              </Box>
                        }
                      </Box>
                }
              </Box>
            : null
        }
        {
          (!sectionService.getSectionMoving() || sectionService.getMovingSectionId() !== props.label) || props.isNonSection
            ? !sectionService.getExpandSectionIds().includes(props.label)
              ? <ul id={'list-container-items'} className={removePaddingApply() ? 'no-padding' : ''}>{props.children}</ul>
              : <ul id={'list-container-items'}>{props.children}</ul>
            : null
        }
      </Component>
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={'Delete Section'}
            message={`Are you sure you want to delete ${sectionService.findSectionById(props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? [], props.label ?? '')?.name} section?`}
            handleSubmit={deleteSection}
            loading={loadingConfirmBtn}/>
      }
    </>
  );
}
);

export default observer(Container);
